import { useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { CircleAlert, Lock, Mail } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useAnalytics, useLogger } from '@/observability';
import './auth.css';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import z from 'zod';
import logo from '/Logo_Simples_OK_22.png';

const loginSearchSchema = z.object({
  redirect: z.string().optional(),
});

export const Route = createFileRoute('/login/')({
  validateSearch: (search) => loginSearchSchema.parse(search),
  component: LoginPage,
});

const schema = z.object({
  email: z
    .string()
    .nonempty({ error: 'Por favor, insira o e-mail.' })
    .nonoptional({ error: 'Por favor, insira o e-mail.' }),
  senha: z
    .string()
    .nonempty({ error: 'Por favor, insira a senha.' })
    .nonoptional({ error: 'Por favor, insira a senha.' }),
});

type TSchema = z.infer<typeof schema>;

function LoginPage() {
  const navigate = useNavigate();
  const { redirect: redirectTo } = Route.useSearch();

  const { login } = useAuth();
  const analytics = useAnalytics();
  const logger = useLogger();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<TSchema>({
    resolver: zodResolver(schema),
  });

  const [message, setMessage] = useState('');
  const [isError, setIsError] = useState(false);

  const handleLogin = async (data: TSchema) => {
    setMessage('');
    setIsError(false);
    analytics.track('Login Attempted');

    try {
      const success = await login(data.email, data.senha);

      if (success) {
        setMessage('Login realizado com sucesso!');
        setIsError(false);

        setTimeout(() => {
          void navigate({ to: redirectTo ?? '/' });
        }, 1000);
      } else {
        analytics.track('Login Failed');
        setMessage('Senha incorreta. Tente novamente.');
        setIsError(true);
      }
    } catch {
      analytics.track('Login Failed');
      logger.error('Login page connection error');
      setMessage('Erro ao conectar ao servidor.');
      setIsError(true);
    }
  };

  return (
    <div className="auth-page">
      <main className="auth-card">
        <div className="auth-brand">
          <img
            src={logo}
            alt="Logo Mobiletec Sistemas"
            className="auth-logo"
          />
          <h1 className="auth-title">Acesso ao Sistema</h1>
          <p className="auth-subtitle">Gerenciamento Logístico de Precisão</p>
        </div>

        {message && (
          <p
            className={isError ? 'auth-alert auth-alert--error' : 'auth-alert auth-alert--success'}
            role="alert"
          >
            {isError && (
              <CircleAlert
                className="auth-icon auth-icon--alert"
                aria-hidden="true"
              />
            )}
            {message}
          </p>
        )}

        <form
          className="auth-form"
          onSubmit={(event) => {
            void handleSubmit(handleLogin)(event);
          }}
        >
          <div className="auth-field-group">
            <label
              className="auth-label"
              htmlFor="email"
            >
              E-mail
            </label>
            <div className={`auth-field${errors.email ? ' auth-field--error' : ''}`}>
              <Mail
                className="auth-icon"
                aria-hidden="true"
              />
              <input
                id="email"
                type="email"
                placeholder="Digite seu e-mail"
                autoComplete="email"
                {...register('email')}
              />
            </div>
            {errors.email?.message && (
              <p className="auth-field-error">
                <CircleAlert
                  className="auth-icon auth-icon--alert"
                  aria-hidden="true"
                />
                {errors.email.message}
              </p>
            )}
          </div>

          <div className="auth-field-group">
            <label
              className="auth-label"
              htmlFor="senha"
            >
              Senha
            </label>
            <div className={`auth-field${errors.senha ? ' auth-field--error' : ''}`}>
              <Lock
                className="auth-icon"
                aria-hidden="true"
              />
              <input
                id="senha"
                type="password"
                placeholder="Digite sua senha"
                autoComplete="current-password"
                {...register('senha')}
              />
            </div>
            {errors.senha?.message && (
              <p className="auth-field-error">
                <CircleAlert
                  className="auth-icon auth-icon--alert"
                  aria-hidden="true"
                />
                {errors.senha.message}
              </p>
            )}
          </div>

          <button
            type="submit"
            className="auth-submit"
          >
            Entrar
          </button>
        </form>
      </main>
    </div>
  );
}
